import React, { use, useEffect } from 'react';
import { Creative, CreativeType } from '../../../../types/creative';
import {
    CreativeMediaVariant,
    CreativeMediaVariantStatus,
    CreativeMediaVariantType,
} from '../../../../types/creative-media-variant';
import Select from 'react-select';

interface PreviewCreativeModalProps {
    creative: Creative;
    isOpen: boolean;
    toggle: () => void;
}

const PreviewCreativeModal = ({ creative, isOpen, toggle }: PreviewCreativeModalProps) => {
    const [selectedMediaVariant, setSelectedMediaVariant] = React.useState<any>();
    const [mediaVariantOptions, setMediaVariantOptions] = React.useState<any[]>([]);
    const [iframeSrcDoc, setIframeSrcDoc] = React.useState<string>('');
    const isEveryMediaVariantReady = creative.mediaVariants?.every((mediaVariant) => mediaVariant.status === CreativeMediaVariantStatus.READY)
    const toggleModal = () => {
        toggle();
    };

    useEffect(() => {
        if (creative.mediaVariants && creative.mediaVariants.length > 0) {
            const options = creative.mediaVariants
                .filter(variant => variant.variantType !== CreativeMediaVariantType.DASH)
                .map(variant => ({
                    value: variant.id,
                    label: `${variant.variantType} - ${variant.width || 0}x${variant.height || 0} (${variant.mimeType})`,
                    ...variant,
                }));
            setMediaVariantOptions(options);
            setSelectedMediaVariant(options[0]);
            // Atualiza o iframeSrcDoc ao inicializar
            if (options[0]) {
                setIframeSrcDoc(iframeMedia(options[0]));
            }
        } else {
            setMediaVariantOptions([]);
            setSelectedMediaVariant(null);
            setIframeSrcDoc('');
        }
    }, [creative]);

    // Atualize o handler para receber o valor selecionado
    const onMediaVariantChange = (option: any) => {
        setSelectedMediaVariant(option);
        if (option) {
            setIframeSrcDoc(iframeMedia(option));
        } else {
            setIframeSrcDoc('');
        }
    }

    const iframeMedia = (mediaVariant: CreativeMediaVariant) => {
        if (!mediaVariant || !mediaVariant.url) {
            console.error('Media variant is not ready or URL is missing');
            return '';
        }

        return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>Title</title>
    <!-- Video.js CSS -->
    <link href="https://vjs.zencdn.net/8.10.0/video-js.css" rel="stylesheet" />
    <link href="https://cdnjs.cloudflare.com/ajax/libs/videojs-contrib-ads/7.3.0/videojs-contrib-ads.css" rel="stylesheet" />
    <style>
        body, html {
            margin: 0;
            padding: 0;
            height: 100%;
            overflow: hidden;
        }
        #videojs-vast-player {
            width: 100%;
            height: 100%;
        }
    </style>
</head>
<body>
    <video id="videojs-player" class="video-js vjs-default-skin" controls width="640" height="360" preload="auto"></video>

    <!-- Video.js JS -->
    <script src="https://vjs.zencdn.net/8.10.0/video.min.js"></script>
    <!-- videojs-contrib-ads -->
    <script src="https://cdnjs.cloudflare.com/ajax/libs/videojs-contrib-ads/7.3.0/videojs-contrib-ads.min.js"></script>
    <!-- videojs-ima (Google IMA SDK for VAST) -->
    <script src="https://imasdk.googleapis.com/js/sdkloader/ima3.js"></script>
    <script src="https://cdn.jsdelivr.net/npm/videojs-ima@1.9.0/dist/videojs.ima.min.js"></script>
    <!-- videojs-http-streaming (HLS) -->
    <script src="https://cdn.jsdelivr.net/npm/videojs-http-streaming@3.2.2/dist/videojs-http-streaming.min.js"></script>
    <!-- videojs-contrib-dash (DASH) -->
    <script src="https://cdn.jsdelivr.net/npm/videojs-contrib-dash@2.11.0/dist/videojs-dash.min.js"></script>
    <!-- dash.js (required for DASH playback) -->
    <script src="https://cdn.dashjs.org/latest/dash.all.min.js"></script>

    <script>
        var player = videojs('videojs-player');
        player.on('adserror', function(event) {
            console.error('Ad error:', event);
        });
        player.ready(function() {
            player.src({
                src: '${mediaVariant.url}',
                type: '${mediaVariant.mimeType}'
            });
        });
    </script>
</body>
</html>`
    };

    return (
        <div className={`modal ${isOpen ? 'show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered modal-xl modal-fullscreen-xl-down">
                <div className="modal-content">
                    <div className="modal-header bg-light p-3">
                        <h5 className="modal-title">Pré-visualizar criativo</h5>
                        <button type="button" className="btn-close" onClick={toggleModal}></button>
                    </div>
                    <div className="modal-body">
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col d-flex justify-content-center align-items-center"
                                     style={{ height: '100%' }}>
                                    {isOpen && (creative.type === CreativeType.HTML5 || creative.type === CreativeType.IMAGE) && creative.url && (
                                        (() => {
                                            const [width, height] = creative.size?.split('x') ?? [creative.mediaWidth, creative.mediaHeight];
                                            return (
                                                <iframe
                                                    className="overflow-hidden"
                                                    src={creative.url}
                                                    width={width}
                                                    height={height}
                                                    title="Creative preview"
                                                ></iframe>
                                            );
                                        })()
                                    )}
                                    {isOpen &&
                                        (creative.type === CreativeType.VIDEO_VAST || creative.type === CreativeType.AUDIO_VAST) &&
                                        isEveryMediaVariantReady && (<>
                                            <iframe
                                                width="640"
                                                height="360"
                                                srcDoc={iframeSrcDoc}
                                                sandbox="allow-scripts allow-same-origin allow-top-navigation">
                                            </iframe>
                                        </>)}
                                </div>
                            </div>
                            { mediaVariantOptions.length > 0 && (
                                <div className="row mt-5">
                                    <div className="col">
                                        <Select
                                            value={selectedMediaVariant}
                                            options={mediaVariantOptions}
                                            onChange={onMediaVariantChange}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={toggleModal}>Fechar</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PreviewCreativeModal;